import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import './ResetPassword.css';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;
  const verified = location.state?.verified;

  useEffect(() => {
    if (!email || !verified) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, verified, navigate]);

  const validateRealTime = (password) => {
    const errors = [];
    if (password.length < 6) errors.push('At least 6 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    setPasswordErrors(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(', ').toLowerCase()}`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email, newPassword, confirmPassword);
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password reset successful! Please login with your new password.' }
        });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!email || !verified) return null;

  return (
    <div className="rp-container">
      <div className="rp-bg">
        <div className="rp-bg-gradient"></div>
      </div>

      <div className="rp-card">
        <div className="rp-icon">
          <Lock size={28} />
        </div>

        <h2 className="rp-title">Set New Password</h2>
        <p className="rp-subtitle">
          Create a strong password for your account.
        </p>

        {success && (
          <div className="rp-alert-success">
            <CheckCircle size={15} />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="rp-alert-error">
            <XCircle size={15} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="rp-form">
          <div className="rp-input-group">
            <label className="rp-label">New Password</label>
            <div className="rp-password-wrapper">
              <Lock size={16} className="rp-lock-icon" />
              <input
                type={showNew ? 'text' : 'password'}
                className="rp-input"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validateRealTime(e.target.value);
                  setError('');
                }}
                required
                autoFocus
              />
              <button
                type="button"
                className="rp-toggle"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordErrors.length > 0 && newPassword.length > 0 && (
              <div className="rp-requirements">
                {passwordErrors.map((err, idx) => (
                  <span key={idx} className="rp-req-badge">{err}</span>
                ))}
              </div>
            )}
          </div>

          <div className="rp-input-group">
            <label className="rp-label">Confirm Password</label>
            <div className="rp-password-wrapper">
              <Lock size={16} className="rp-lock-icon" />
              <input
                type={showConfirm ? 'text' : 'password'}
                className={`rp-input ${
                  confirmPassword && newPassword !== confirmPassword ? 'input-error' : ''
                }`}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
              />
              <button
                type="button"
                className="rp-toggle"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <div className="rp-error-text">Passwords do not match</div>
            )}
            {confirmPassword && newPassword === confirmPassword && newPassword.length > 0 && (
              <div className="rp-success-text">✓ Passwords match</div>
            )}
          </div>

          <button type="submit" className="rp-btn" disabled={loading || !!success}>
            {loading ? (
              <span className="rp-btn-loading">
                <span className="rp-spinner"></span>
                Resetting...
              </span>
            ) : 'Reset Password'}
          </button>
        </form>

        <div className="rp-back">
          <Link to="/login">
            <ArrowLeft size={15} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;